import { z } from "zod";

export enum Connector {
  Stripe = "stripe",
  GoogleAnalytics = "google-analytics",
  GoogleDrive = "google-drive",
  Zapier = "zapier",
}

export type NangoConnector =
  | Connector.GoogleAnalytics
  | Connector.GoogleDrive
  | Connector.Stripe;

export type ApiKeyConnector = Connector.Zapier;
export const apiKeyConnectors = [Connector.Zapier];

export enum NangoModelName {
  GAReportPerformanceOverview = "GAReportPerformanceOverview",
  GAReportTrafficOverview = "GAReportTrafficOverview",
  GAReportTopContent = "GAReportTopContent",
  GAReportTrafficSource = "GAReportTrafficSource",
  GAReportEngagement = "GAReportEngagement",
  GDDocument = "Document",
}

export enum NangoSyncName {
  GAReportsPerformance = "ga-reports-performance-overview",
  GAReportsOverTime = "ga-reports-over-time",
  GDDocuments = "documents",
}

export enum NangoActionName {
  GDFetchGoogleDoc = "fetch-google-doc",
  GDFetchGoogleSheet = "fetch-google-sheet",
}

export const NangoIntegrationIds = {
  [Connector.Stripe]: "com.noan.stripe-connector",
  [Connector.GoogleAnalytics]: "com.noan.ga-connector",
  [Connector.GoogleDrive]: "com.noan.gd-connector",
} as const;

export const nangoIntegrations = Object.values(NangoIntegrationIds);

const FindConnectionFilterSchema = z.object({
  connector: z.nativeEnum(Connector),
  includeRemoved: z.boolean().optional(),
});

const NangoConnectionSchema = z.object({
  nangoConnectionId: z.string().uuid(),
  nangoIntegrationId: z.string(),
});

export const NangoConnectionGoogleDriveMetadataSchema = z.object({
  folders: z.array(z.string()).optional(),
  files: z.array(z.string()).optional(),
});

const StripeConnectorConfigurationSchema = z
  .object({
    connector: z.literal(Connector.Stripe),
  })
  .merge(NangoConnectionSchema);

const GoogleAnalyticsConnectorConfigurationSchema = z
  .object({
    connector: z.literal(Connector.GoogleAnalytics),
    /**
     * The Google Analytics account the organization is connected to. Can be a website or an app.
     */
    connectedAccount: z
      .object({
        id: z.string(),
        name: z.string(),
        propertyName: z.string(),
        propertyDisplayName: z.string(),
      })
      .optional(),
  })
  .merge(NangoConnectionSchema);

export const GoogleDriveConnectorConfigurationSchema = z
  .object({
    connector: z.literal(Connector.GoogleDrive),
  })
  .merge(NangoConnectionSchema);

export const GoogleDriveDocumentMetadataSchema = z.object({
  id: z.string(),
  mimeType: z.string(),
  title: z.string(),
  updatedAt: z.string(),
  url: z.string(),
});

export const NangoGoogleSheetSchema = z.object({
  spreadsheetId: z.string(),
  properties: z.object({
    title: z.string(),
    locale: z.string(),
    timezone: z.string(),
  }),
  sheets: z.array(
    z.object({
      properties: z.object({
        sheetId: z.string(),
        title: z.string(),
        index: z.number(),
      }),
      data: z.array(
        z.object({
          rowData: z.array(
            z.object({
              values: z.array(z.object({})),
            }),
          ),
        }),
      ),
    }),
  ),
});
export type NangoGoogleSheet = z.infer<typeof NangoGoogleSheetSchema>;

const ZapierConnectorConfigurationSchema = z.object({
  connector: z.literal(Connector.Zapier),
  apiKey: z.string(),
  maskedApiKey: z.string(),
});

export const ConnectorConfigurationSchema = z.discriminatedUnion("connector", [
  StripeConnectorConfigurationSchema,
  GoogleAnalyticsConnectorConfigurationSchema,
  GoogleDriveConnectorConfigurationSchema,
  ZapierConnectorConfigurationSchema,
]);

export const NangoConnectorConfigurationSchema = z.discriminatedUnion(
  "connector",
  [
    StripeConnectorConfigurationSchema,
    GoogleAnalyticsConnectorConfigurationSchema,
    GoogleDriveConnectorConfigurationSchema,
  ],
);

export type FindConnectionFilter = z.infer<typeof FindConnectionFilterSchema>;

export type GoogleAnalyticsConnectorConfiguration = z.infer<
  typeof GoogleAnalyticsConnectorConfigurationSchema
>;
export type GoogleDriveConnectorConfiguration = z.infer<
  typeof GoogleDriveConnectorConfigurationSchema
>;
export type GoogleDriveDocumentMetadata = z.infer<
  typeof GoogleDriveDocumentMetadataSchema
>;
export type NangoConnectionGoogleDriveMetadata = z.infer<
  typeof NangoConnectionGoogleDriveMetadataSchema
>;
export type StripeConnectorConfiguration = z.infer<
  typeof StripeConnectorConfigurationSchema
>;
export type ZapierConnectorConfiguration = z.infer<
  typeof ZapierConnectorConfigurationSchema
>;

export type ConnectorConfiguration = z.infer<
  typeof ConnectorConfigurationSchema
>;

export type NangoConnection = z.infer<typeof NangoConnectionSchema>;

export type NangoConnectorConfiguration = z.infer<
  typeof NangoConnectorConfigurationSchema
>;

export function isConnectorGoogleAnalytics(
  configuration?: ConnectorConfiguration,
) {
  return configuration?.connector === Connector.GoogleAnalytics;
}
export function isConnectorGoogleDrive(configuration?: ConnectorConfiguration) {
  return configuration?.connector === Connector.GoogleDrive;
}
export function isConnectorStripe(configuration?: ConnectorConfiguration) {
  return configuration?.connector === Connector.Stripe;
}
export function isConnectorZapier(configuration?: ConnectorConfiguration) {
  return configuration?.connector === Connector.Zapier;
}
